import React, { useState, useEffect } from "react";
import { Box, Flex, Label, Grid } from "theme-ui";
import Separator from "components/ui/Separator";
import H3 from "components/typography/H3";
import H5 from "components/typography/H5";
import P from "components/typography/P";
import { useToast } from "components/toast";
import { themevals } from "theme/themevals";
import Switch from "react-switch";
import { getActivitiesByModule } from "api/ActivityTracker";
import Subtext from "components/typography/Subtext";
import AnnouncementsCard from "./AnnouncementsCard";

const Popups = ({
  company,
  values,
  errors,
  submitCount,
  setFieldValue,
  initCompany,
}: {
  company: any;
  values: any;
  errors: any;
  submitCount: number;
  setFieldValue: (i: string, v: any) => void;
  initCompany: () => void;
}) => {
  const [discountPopupEnabled, setDiscountPopupEnabled] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [metrics, setMetrics] = useState({
    viewed: 0,
    reminderRemoved: 0,
    invoiceSent: 0,
    cancelled: 0,
  });

  const toast = useToast();

  useEffect(() => {
    if (company?.id) {
      // Initialize the discount_popup setting from company data
      // Database stores as 0 (disabled) or 1 (enabled)
      // But JSON parsing may convert to string "0" or "1"
      console.log('Company discount_popup value:', company?.data?.discount_popup, 'Type:', typeof company?.data?.discount_popup);
      
      // Enabled if value is 1, "1", true, or not set (null/undefined)
      // Disabled only if value is 0, "0", false, or "false"
      const value = company?.data?.discount_popup;
      const isEnabled = value !== 0 && value !== "0" && value !== false && value !== "false";
      console.log('isEnabled calculated as:', isEnabled);
      setDiscountPopupEnabled(isEnabled);

      // Set the form value
      setFieldValue("data.discount_popup", isEnabled);
    }
    setLoadingSettings(false);
  }, [company?.id]);

  // Fetch activity data from backend
  useEffect(() => {
    const fetchActivities = async () => {
      if (!company?.id) return;

      setLoadingActivities(true);

      const activities = await getActivitiesByModule({
        module: "discount",
        companyId: company.id,
        limit: 1000, // Get all activities to calculate metrics
      });

      if (activities) {
        // Calculate metrics
        const viewed = activities.filter((a: any) => a.action === "discount_popup_viewed").length;
        const reminderRemoved = activities.filter((a: any) => a.action === "discount_no_reminder").length;
        const invoiceSent = activities.filter((a: any) => a.action === "invoice_sent_from_discount_modal").length;
        const cancelled = activities.filter((a: any) => a.action === "discount_popup_cancelled").length;

        setMetrics({
          viewed,
          reminderRemoved,
          invoiceSent,
          cancelled,
        });
      }

      setLoadingActivities(false);
    };

    fetchActivities();
  }, [company?.id]);

  const onToggleDiscountPopup = async (checked: boolean) => {
    setDiscountPopupEnabled(checked);
    setFieldValue("data.discount_popup", checked);

    toast.add(checked ? "Discount popup enabled. Save to apply changes." : "Discount popup disabled. Save to apply changes.", "var(--theme-ui-colors-info)");
  };

  const MetricBox = ({ label, value }: { label: string; value: number }) => (
    <Box sx={{ flex: "1", minWidth: "0" }}>
      <P fontFamily={"Poppins"} fontSize={"12px"} fontWeight={500} color={themevals.colors.dkgrey} marginBottom={"8px"} marginTop={"0px"}>
        {label}
      </P>
      <P fontFamily={"Poppins"} fontSize={"14px"} fontWeight={500} marginBottom={"0px"} marginTop={"0px"}>
        {value}
      </P>
    </Box>
  );

  return (
    <>
      <Box sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <H3 marginTop={"10px"} marginBottom={0} fontSize={"16px"} fontWeight={"600"} fontFamily={"Poppins"}>
          Popups
        </H3>
      </Box>
      <Separator />

      <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <Box>
          <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
            Available Popups
          </H5>
          <Subtext marginTop={0} marginBottom={"30px"} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
            View metrics and control popups for your company.
          </Subtext>
        </Box>

        {/* Discount Reminder Section */}
        <Box>
          {/* Card border */}
          <Box sx={{ border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px" }}>
            {/* Header with toggle */}
            <Flex sx={{ justifyContent: "space-between", alignItems: "center", paddingTop: "20px", padding: "20px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
              <H5 fontSize="14px" marginBottom={"0px"} marginTop="0px" fontFamily="Poppins" fontWeight="600">
                Discount Reminder
              </H5>
              <Switch
                checked={discountPopupEnabled}
                onChange={onToggleDiscountPopup}
                onColor={themevals.colors.success}
                offColor="#ccc"
                uncheckedIcon={false}
                checkedIcon={false}
                height={15}
                width={26}
                handleDiameter={13}
                disabled={loadingSettings}
              />
            </Flex>

            {/* Metrics Row */}
            <Grid columns={[1, "1fr 1fr"]} sx={{ gap: "30px", flexWrap: "wrap", padding: "20px", justifyContent: "space-between" }}>
              <MetricBox label="Viewed" value={metrics.viewed} />
              <MetricBox label="Reminder Removed" value={metrics.reminderRemoved} />
              <MetricBox label="Invoice Sent" value={metrics.invoiceSent} />
              <MetricBox label="Cancelled" value={metrics.cancelled} />
            </Grid>
          </Box>

          {/* Announcements ("What's new" at login), 2026-10-09 */}
          <AnnouncementsCard />
        </Box>
      </Grid>
    </>
  );
};

export default Popups;
